
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import RedirectHandler from "@/components/RedirectHandler";
import LegalChatbot from "@/components/LegalChatbot";
import FloatingPhoneButton from "@/components/FloatingPhoneButton";
import AdminRoute from "@/components/admin/AdminRoute";
import { generalRoutes, practiceAreaRoutes, immigrationRoutes } from "@/lib/route-mapping";
import Index from "./pages/Index";
import AboutUs from "./pages/AboutUs";
import PracticeAreas from "./pages/PracticeAreas";
import ImmigrationServices from "./pages/ImmigrationServices";
import AsylumServices from "./pages/immigration/AsylumServices";
import CitizenshipNaturalization from "./pages/immigration/CitizenshipNaturalization";
import FamilyBasedImmigration from "./pages/immigration/FamilyBasedImmigration";
import EmploymentBasedImmigration from "./pages/immigration/EmploymentBasedImmigration";
import DeportationRemovalDefense from "./pages/immigration/DeportationRemovalDefense";
import Waivers from "./pages/immigration/Waivers";
import DACA from "./pages/immigration/DACA";
import K1FianceVisa from "./pages/immigration/K1FianceVisa";
import VAWAPetitions from "./pages/immigration/VAWAPetitions";
import HumanitarianParoleTPS from "./pages/immigration/HumanitarianParoleTPS";
import GreenCardRenewal from "./pages/immigration/GreenCardRenewal";
import ConsularProcessing from "./pages/immigration/ConsularProcessing";
import UVisa from "./pages/immigration/UVisa";
import AdvanceParole from "./pages/immigration/AdvanceParole";
import ReEntryPermit from "./pages/immigration/ReEntryPermit";
import EB5InvestorVisa from "./pages/immigration/EB5InvestorVisa";
import StudentVisas from "./pages/immigration/StudentVisas";
import CriminalDefense from "./pages/CriminalDefense";
import DWI from "./pages/criminal-defense/DWI";
import DrugCharges from "./pages/criminal-defense/DrugCharges";
import AssaultFamilyViolence from "./pages/criminal-defense/AssaultFamilyViolence";
import ViolentCrimes from "./pages/criminal-defense/ViolentCrimes";
import TheftRobbery from "./pages/criminal-defense/TheftRobbery";
import SexCrimes from "./pages/criminal-defense/SexCrimes";
import ExpunctionNondisclosure from "./pages/criminal-defense/ExpunctionNondisclosure";
import PersonalInjury from "./pages/PersonalInjury";
import AutoAccident from "./pages/personal-injury/AutoAccident";
import TruckAccident from "./pages/personal-injury/TruckAccident";
import IndustrialAccident from "./pages/personal-injury/IndustrialAccident";
import MedicalMalpractice from "./pages/personal-injury/MedicalMalpractice";
import Divorce from "./pages/family-law/Divorce";
import ChildCustody from "./pages/family-law/ChildCustody";
import ChildSupport from "./pages/family-law/ChildSupport";
import CPSDefense from "./pages/family-law/CPSDefense";
import Adoption from "./pages/family-law/Adoption";
import CourtOrderModification from "./pages/family-law/CourtOrderModification";
import FamilyLaw from "./pages/FamilyLaw";
import TaxLaw from "./pages/TaxLaw";
import TaxLitigation from "./pages/tax/TaxLitigation";
import TaxPlanning from "./pages/tax/TaxPlanning";
import TaxDebtResolution from "./pages/tax/TaxDebtResolution";
import ProbateEstate from "./pages/ProbateEstate";
import ProbateAdministration from "./pages/probate-estate/ProbateAdministration";
import EstatePlanning from "./pages/probate-estate/EstatePlanning";
import SpecialNeedsTrust from "./pages/probate-estate/SpecialNeedsTrust";
import Guardianship from "./pages/probate-estate/Guardianship";
import RealEstateLaw from "./pages/RealEstateLaw";
import BuyingSelling from "./pages/real-estate/BuyingSelling";
import TitleSearch from "./pages/real-estate/TitleSearch";
import ContractReview from "./pages/real-estate/ContractReview";
import AffidavitsHeirship from "./pages/real-estate/AffidavitsHeirship";
import LienRelease from "./pages/real-estate/LienRelease";
import LeaseNegotiations from "./pages/real-estate/LeaseNegotiations";
import Closings from "./pages/real-estate/Closings";
import ForeclosuresEvictions from "./pages/real-estate/ForeclosuresEvictions";
import BusinessLaw from "./pages/BusinessLaw";
import BusinessFormation from "./pages/business-law/BusinessFormation";
import PartnershipAgreements from "./pages/business-law/PartnershipAgreements";
import TrademarkRegistration from "./pages/business-law/TrademarkRegistration";
import PurchaseSaleBusiness from "./pages/business-law/PurchaseSaleBusiness";
import RegisteredAgent from "./pages/business-law/RegisteredAgent";
import DebtCollection from "./pages/business-law/DebtCollection";
import InsuranceClaims from "./pages/business-law/InsuranceClaims";
import NonprofitFormation from "./pages/business-law/NonprofitFormation";
import BusinessClosing from "./pages/business-law/BusinessClosing";
import Contact from "./pages/Contact";
import Consultation from "./pages/Consultation";
import Booking from "./pages/Booking";
import BookingSuccess from "./pages/booking/Success";
import Tools from "./pages/Tools";
import Blog from "./pages/Blog";
import AsylumProcess2025 from "./pages/blog/AsylumProcess2025";
import DeportationDefense from "./pages/blog/DeportationDefense";
import CitizenshipGuide from "./pages/blog/CitizenshipGuide";
import PoliceRights from "./pages/blog/PoliceRights";
import PersonalInjuryClaim from "./pages/blog/PersonalInjuryClaim";
import DivorceTexas from "./pages/blog/DivorceTexas";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import NotFound from "./pages/NotFound";
import TheWoodlands from "./pages/areas/TheWoodlands";
import RiverOaks from "./pages/areas/RiverOaks";
import Montrose from "./pages/areas/Montrose";
import Heights from "./pages/areas/Heights";
import WestUniversity from "./pages/areas/WestUniversity";
import SugarLand from "./pages/areas/SugarLand";
import Katy from "./pages/areas/Katy";
import Midtown from "./pages/areas/Midtown";
import MedicalCenter from "./pages/areas/MedicalCenter";
import Memorial from "./pages/areas/Memorial";
import Cypress from "./pages/areas/Cypress";
import Uptown from "./pages/areas/Uptown";
import Downtown from "./pages/areas/Downtown";
import EnergyCorridor from "./pages/areas/EnergyCorridor";
import GreaterSouthwest from "./pages/areas/GreaterSouthwest";
import SpringBranch from "./pages/areas/SpringBranch";
import AdminBookings from "./pages/admin/Bookings";
import AdminDashboard from "./pages/admin/Dashboard";
import SEOValidation from "./pages/admin/SEOValidation";
import AdminLogin from "./pages/AdminLogin";
import SEO from "@/components/SEO";
import URLIndex from "./pages/admin/URLIndex";
import SiteMap from "./pages/admin/SiteMap";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BrowserRouter>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <RedirectHandler />
            <LegalChatbot />
            <FloatingPhoneButton />
            <SEO />
            <Routes>
              {/* Home - All Languages */}
              <Route path={generalRoutes.home.en} element={<Index />} />
              <Route path={generalRoutes.home.es} element={<Index />} />
              <Route path={generalRoutes.home.vi} element={<Index />} />
              
              {/* About - All Languages */}
              <Route path={generalRoutes.about.en} element={<AboutUs />} />
              <Route path={generalRoutes.about.es} element={<AboutUs />} />
              <Route path={generalRoutes.about.vi} element={<AboutUs />} />
              
              {/* Practice Areas - All Languages */}
              <Route path={generalRoutes.practiceAreas.en} element={<PracticeAreas />} />
              <Route path={generalRoutes.practiceAreas.es} element={<PracticeAreas />} />
              <Route path={generalRoutes.practiceAreas.vi} element={<PracticeAreas />} />
              
              {/* Contact - All Languages */}
              <Route path={generalRoutes.contact.en} element={<Contact />} />
              <Route path={generalRoutes.contact.es} element={<Contact />} />
              <Route path={generalRoutes.contact.vi} element={<Contact />} />
              
              {/* Consultation - All Languages */}
              <Route path={generalRoutes.consultation.en} element={<Consultation />} />
              <Route path={generalRoutes.consultation.es} element={<Consultation />} />
              <Route path={generalRoutes.consultation.vi} element={<Consultation />} />
              
              {/* Booking - All Languages */}
              <Route path={generalRoutes.booking.en} element={<Booking />} />
              <Route path={generalRoutes.booking.es} element={<Booking />} />
              <Route path={generalRoutes.booking.vi} element={<Booking />} />
              
              {/* Blog - All Languages */}
              <Route path={generalRoutes.blog.en} element={<Blog />} />
              <Route path={generalRoutes.blog.es} element={<Blog />} />
              <Route path={generalRoutes.blog.vi} element={<Blog />} />
              
              {/* Immigration Services - All Languages */}
              <Route path={practiceAreaRoutes.immigration.en} element={<ImmigrationServices />} />
              <Route path={practiceAreaRoutes.immigration.es} element={<ImmigrationServices />} />
              <Route path={practiceAreaRoutes.immigration.vi} element={<ImmigrationServices />} />
              
              {/* Criminal Defense - All Languages */}
              <Route path={practiceAreaRoutes.criminal.en} element={<CriminalDefense />} />
              <Route path={practiceAreaRoutes.criminal.es} element={<CriminalDefense />} />
              <Route path={practiceAreaRoutes.criminal.vi} element={<CriminalDefense />} />
              
              {/* Personal Injury - All Languages */}
              <Route path={practiceAreaRoutes.personalInjury.en} element={<PersonalInjury />} />
              <Route path={practiceAreaRoutes.personalInjury.es} element={<PersonalInjury />} />
              <Route path={practiceAreaRoutes.personalInjury.vi} element={<PersonalInjury />} />
              
              {/* Family Law - All Languages */}
              <Route path={practiceAreaRoutes.family.en} element={<FamilyLaw />} />
              <Route path={practiceAreaRoutes.family.es} element={<FamilyLaw />} />
              <Route path={practiceAreaRoutes.family.vi} element={<FamilyLaw />} />
              
              {/* Tax Law - All Languages */}
              <Route path={practiceAreaRoutes.tax.en} element={<TaxLaw />} />
              <Route path={practiceAreaRoutes.tax.es} element={<TaxLaw />} />
              <Route path={practiceAreaRoutes.tax.vi} element={<TaxLaw />} />
              
              {/* Probate & Estate - All Languages */}
              <Route path={practiceAreaRoutes.probate.en} element={<ProbateEstate />} />
              <Route path={practiceAreaRoutes.probate.es} element={<ProbateEstate />} />
              <Route path={practiceAreaRoutes.probate.vi} element={<ProbateEstate />} />
              
              {/* Real Estate Law - All Languages */}
              <Route path={practiceAreaRoutes.realEstate.en} element={<RealEstateLaw />} />
              <Route path={practiceAreaRoutes.realEstate.es} element={<RealEstateLaw />} />
              <Route path={practiceAreaRoutes.realEstate.vi} element={<RealEstateLaw />} />
              
              {/* Business Law - All Languages */}
              <Route path={practiceAreaRoutes.business.en} element={<BusinessLaw />} />
              <Route path={practiceAreaRoutes.business.es} element={<BusinessLaw />} />
              <Route path={practiceAreaRoutes.business.vi} element={<BusinessLaw />} />
              
              {/* Immigration Sub-Pages - All Languages */}
              <Route path={immigrationRoutes.asylumPage.en} element={<AsylumServices />} />
              <Route path={immigrationRoutes.asylumPage.es} element={<AsylumServices />} />
              <Route path={immigrationRoutes.asylumPage.vi} element={<AsylumServices />} />
              
              <Route path={immigrationRoutes.citizenship.en} element={<CitizenshipNaturalization />} />
              <Route path={immigrationRoutes.citizenship.es} element={<CitizenshipNaturalization />} />
              <Route path={immigrationRoutes.citizenship.vi} element={<CitizenshipNaturalization />} />
              
              <Route path={immigrationRoutes.familyBased.en} element={<FamilyBasedImmigration />} />
              <Route path={immigrationRoutes.familyBased.es} element={<FamilyBasedImmigration />} />
              <Route path={immigrationRoutes.familyBased.vi} element={<FamilyBasedImmigration />} />
              
              <Route path={immigrationRoutes.employmentBased.en} element={<EmploymentBasedImmigration />} />
              <Route path={immigrationRoutes.employmentBased.es} element={<EmploymentBasedImmigration />} />
              <Route path={immigrationRoutes.employmentBased.vi} element={<EmploymentBasedImmigration />} />
              
              <Route path={immigrationRoutes.deportation.en} element={<DeportationRemovalDefense />} />
              <Route path={immigrationRoutes.deportation.es} element={<DeportationRemovalDefense />} />
              <Route path={immigrationRoutes.deportation.vi} element={<DeportationRemovalDefense />} />
              
              <Route path={immigrationRoutes.waivers.en} element={<Waivers />} />
              <Route path={immigrationRoutes.waivers.es} element={<Waivers />} />
              <Route path={immigrationRoutes.waivers.vi} element={<Waivers />} />
              
              <Route path={immigrationRoutes.daca.en} element={<DACA />} />
              <Route path={immigrationRoutes.daca.es} element={<DACA />} />
              <Route path={immigrationRoutes.daca.vi} element={<DACA />} />
              
              <Route path={immigrationRoutes.k1Visa.en} element={<K1FianceVisa />} />
              <Route path={immigrationRoutes.k1Visa.es} element={<K1FianceVisa />} />
              <Route path={immigrationRoutes.k1Visa.vi} element={<K1FianceVisa />} />
              
              <Route path={immigrationRoutes.vawa.en} element={<VAWAPetitions />} />
              <Route path={immigrationRoutes.vawa.es} element={<VAWAPetitions />} />
              <Route path={immigrationRoutes.vawa.vi} element={<VAWAPetitions />} />
              
              <Route path={immigrationRoutes.humanitarianParole.en} element={<HumanitarianParoleTPS />} />
              <Route path={immigrationRoutes.humanitarianParole.es} element={<HumanitarianParoleTPS />} />
              <Route path={immigrationRoutes.humanitarianParole.vi} element={<HumanitarianParoleTPS />} />
              
              <Route path={immigrationRoutes.greenCardRenewal.en} element={<GreenCardRenewal />} />
              <Route path={immigrationRoutes.greenCardRenewal.es} element={<GreenCardRenewal />} />
              <Route path={immigrationRoutes.greenCardRenewal.vi} element={<GreenCardRenewal />} />
              
              <Route path={immigrationRoutes.consularProcessing.en} element={<ConsularProcessing />} />
              <Route path={immigrationRoutes.consularProcessing.es} element={<ConsularProcessing />} />
              <Route path={immigrationRoutes.consularProcessing.vi} element={<ConsularProcessing />} />
              
              <Route path={immigrationRoutes.uVisa.en} element={<UVisa />} />
              <Route path={immigrationRoutes.uVisa.es} element={<UVisa />} />
              <Route path={immigrationRoutes.uVisa.vi} element={<UVisa />} />
              
              <Route path={immigrationRoutes.advanceParole.en} element={<AdvanceParole />} />
              <Route path={immigrationRoutes.advanceParole.es} element={<AdvanceParole />} />
              <Route path={immigrationRoutes.advanceParole.vi} element={<AdvanceParole />} />
              
              <Route path={immigrationRoutes.reentryPermit.en} element={<ReEntryPermit />} />
              <Route path={immigrationRoutes.reentryPermit.es} element={<ReEntryPermit />} />
              <Route path={immigrationRoutes.reentryPermit.vi} element={<ReEntryPermit />} />
              
              <Route path={immigrationRoutes.eb5.en} element={<EB5InvestorVisa />} />
              <Route path={immigrationRoutes.eb5.es} element={<EB5InvestorVisa />} />
              <Route path={immigrationRoutes.eb5.vi} element={<EB5InvestorVisa />} />
              
              <Route path={immigrationRoutes.studentVisas.en} element={<StudentVisas />} />
              <Route path={immigrationRoutes.studentVisas.es} element={<StudentVisas />} />
              <Route path={immigrationRoutes.studentVisas.vi} element={<StudentVisas />} />
              {/* Criminal Defense Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/criminal-defense/dwi" element={<DWI />} />
              <Route path="/practice-areas/criminal-defense/drug-charges" element={<DrugCharges />} />
              <Route path="/practice-areas/criminal-defense/assault-family-violence" element={<AssaultFamilyViolence />} />
              <Route path="/practice-areas/criminal-defense/violent-crimes" element={<ViolentCrimes />} />
              <Route path="/practice-areas/criminal-defense/theft-robbery" element={<TheftRobbery />} />
              <Route path="/practice-areas/criminal-defense/sex-crimes" element={<SexCrimes />} />
              <Route path="/practice-areas/criminal-defense/expunction-nondisclosure" element={<ExpunctionNondisclosure />} />
              
              {/* Personal Injury Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/personal-injury/auto-accident" element={<AutoAccident />} />
              <Route path="/practice-areas/personal-injury/truck-accident" element={<TruckAccident />} />
              <Route path="/practice-areas/personal-injury/industrial-accident" element={<IndustrialAccident />} />
              <Route path="/practice-areas/personal-injury/medical-malpractice" element={<MedicalMalpractice />} />
              
              {/* Family Law Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/family-law/divorce" element={<Divorce />} />
              <Route path="/practice-areas/family-law/child-custody" element={<ChildCustody />} />
              <Route path="/practice-areas/family-law/child-support" element={<ChildSupport />} />
              <Route path="/practice-areas/family-law/cps-defense" element={<CPSDefense />} />
              <Route path="/practice-areas/family-law/adoption-parental-termination" element={<Adoption />} />
              <Route path="/practice-areas/family-law/court-order-modification" element={<CourtOrderModification />} />
              
              {/* Tax Law Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/tax/tax-litigation" element={<TaxLitigation />} />
              <Route path="/practice-areas/tax/tax-planning" element={<TaxPlanning />} />
              <Route path="/practice-areas/tax/tax-debt-resolution" element={<TaxDebtResolution />} />
              
              {/* Probate & Estate Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/probate-estate-planning/probate-administration" element={<ProbateAdministration />} />
              <Route path="/practice-areas/probate-estate-planning/estate-planning" element={<EstatePlanning />} />
              <Route path="/practice-areas/probate-estate-planning/special-needs-trust" element={<SpecialNeedsTrust />} />
              <Route path="/practice-areas/probate-estate-planning/guardianship" element={<Guardianship />} />
              
              {/* Real Estate Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/real-estate/buying-selling-residential" element={<BuyingSelling />} />
              <Route path="/practice-areas/real-estate/title-search" element={<TitleSearch />} />
              <Route path="/practice-areas/real-estate/contract-review" element={<ContractReview />} />
              <Route path="/practice-areas/real-estate/affidavits-heirship" element={<AffidavitsHeirship />} />
              <Route path="/practice-areas/real-estate/lien-release" element={<LienRelease />} />
              <Route path="/practice-areas/real-estate/lease-negotiations" element={<LeaseNegotiations />} />
              <Route path="/practice-areas/real-estate/closings" element={<Closings />} />
              <Route path="/practice-areas/real-estate/foreclosures-evictions" element={<ForeclosuresEvictions />} />
              
              {/* Business Law Sub-Pages - TODO: Add to route mapping */}
              <Route path="/practice-areas/business-law/business-formation" element={<BusinessFormation />} />
              <Route path="/practice-areas/business-law/partnership-agreements" element={<PartnershipAgreements />} />
              <Route path="/practice-areas/business-law/trademark-registration" element={<TrademarkRegistration />} />
              <Route path="/practice-areas/business-law/purchase-sale-business" element={<PurchaseSaleBusiness />} />
              <Route path="/practice-areas/business-law/registered-agent" element={<RegisteredAgent />} />
              <Route path="/practice-areas/business-law/debt-collection" element={<DebtCollection />} />
              <Route path="/practice-areas/business-law/insurance-claims" element={<InsuranceClaims />} />
              <Route path="/practice-areas/business-law/nonprofit-formation" element={<NonprofitFormation />} />
              <Route path="/practice-areas/business-law/business-closing" element={<BusinessClosing />} />
              <Route path="/booking/success" element={<BookingSuccess />} />
              
              {/* Admin Routes - No multilingual needed */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
              <Route path="/admin/seo-validation" element={<AdminRoute><SEOValidation /></AdminRoute>} />
              <Route path="/admin/url-index" element={<AdminRoute><URLIndex /></AdminRoute>} />
              <Route path="/admin/sitemap" element={<AdminRoute><SiteMap /></AdminRoute>} />
              
              {/* Other Pages - TODO: Add to route mapping */}
              <Route path="/tools" element={<Tools />} />
              <Route path="/blog/understanding-asylum-process-2025" element={<AsylumProcess2025 />} />
              <Route path="/blog/what-to-do-facing-deportation" element={<DeportationDefense />} />
              <Route path="/blog/citizenship-naturalization-guide" element={<CitizenshipGuide />} />
              <Route path="/blog/rights-during-police-stop" element={<PoliceRights />} />
              <Route path="/blog/handle-personal-injury-claim" element={<PersonalInjuryClaim />} />
              <Route path="/blog/divorce-texas-guide" element={<DivorceTexas />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              
              {/* Areas We Serve - Local SEO Pages */}
              <Route path="/areas-we-serve/the-woodlands" element={<TheWoodlands />} />
              <Route path="/areas-we-serve/river-oaks" element={<RiverOaks />} />
              <Route path="/areas-we-serve/montrose" element={<Montrose />} />
              <Route path="/areas-we-serve/heights" element={<Heights />} />
              <Route path="/areas-we-serve/west-university" element={<WestUniversity />} />
              <Route path="/areas-we-serve/sugar-land" element={<SugarLand />} />
              <Route path="/areas-we-serve/katy" element={<Katy />} />
              <Route path="/areas-we-serve/midtown" element={<Midtown />} />
              <Route path="/areas-we-serve/medical-center" element={<MedicalCenter />} />
              <Route path="/areas-we-serve/memorial" element={<Memorial />} />
              <Route path="/areas-we-serve/cypress" element={<Cypress />} />
              <Route path="/areas-we-serve/uptown" element={<Uptown />} />
              <Route path="/areas-we-serve/downtown" element={<Downtown />} />
              <Route path="/areas-we-serve/energy-corridor" element={<EnergyCorridor />} />
              <Route path="/areas-we-serve/greater-southwest" element={<GreaterSouthwest />} />
              <Route path="/areas-we-serve/spring-branch" element={<SpringBranch />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </LanguageProvider>
      </BrowserRouter>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
